import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TemplateRef } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';

const PANEL_POSITION_BELOW = [
  { originX: 'start' as const, originY: 'bottom' as const, overlayX: 'start' as const, overlayY: 'top' as const },
  { originX: 'start' as const, originY: 'top' as const, overlayX: 'start' as const, overlayY: 'bottom' as const },
];

export interface DropdownOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-custom-dropdown',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  templateUrl: './custom-dropdown.component.html',
  styleUrls: ['./custom-dropdown.component.css'],
})
export class CustomDropdownComponent implements OnDestroy {
  @Input() value: string = '';
  @Input() placeholder: string = 'Seleccionar';
  @Input() options: DropdownOption[] = [];
  @Output() valueChange = new EventEmitter<string>();

  @ViewChild('trigger') triggerRef!: ElementRef<HTMLElement>;
  @ViewChild('panelTemplate') panelTemplate!: TemplateRef<unknown>;

  isOpen = false;

  private overlayRef: OverlayRef | null = null;
  private readonly overlay = inject(Overlay);
  private readonly vcr = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);

  get triggerLabel(): string {
    const selectedItem = this.options.find(opt => opt.value === this.value);
    return selectedItem ? selectedItem.label : this.placeholder;
  }

  openPanel(): void {
    this.isOpen = true;
    this.ensureOverlayAttached();
  }

  closePanel(): void {
    if (this.overlayRef?.hasAttached()) {
      this.overlayRef.detach();
    }
    this.isOpen = false;
  }

  togglePanel(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.isOpen ? this.closePanel() : this.openPanel();
  }

  selectOption(optionValue: string): void {
    this.valueChange.emit(optionValue);
    this.closePanel();
  }

  isSelected(optionValue: string): boolean {
    return this.value === optionValue;
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  private ensureOverlayAttached(): void {
    const origin = this.triggerRef?.nativeElement;
    if (!origin || !this.panelTemplate) return;

    if (!this.overlayRef) {
      const positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(origin)
        .withPositions(PANEL_POSITION_BELOW)
        .withPush(true);

      this.overlayRef = this.overlay.create({
        positionStrategy,
        hasBackdrop: true,
        backdropClass: 'custom-dropdown-backdrop',
        scrollStrategy: this.overlay.scrollStrategies.reposition(), // Allow scroll or close? Close is safer for modals
        panelClass: 'custom-dropdown-overlay-pane',
        width: origin.offsetWidth // Match origin width
      });

      this.overlayRef
        .backdropClick()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.closePanel());

      this.overlayRef
        .keydownEvents()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((ev) => {
          if (ev.key === 'Escape') this.closePanel();
        });

      this.overlayRef
        .detachments()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.isOpen = false;
        });
    }

    // Update width if window resized
    this.overlayRef.updateSize({ width: origin.offsetWidth });

    if (!this.overlayRef.hasAttached()) {
      const portal = new TemplatePortal(this.panelTemplate, this.vcr);
      this.overlayRef.attach(portal);
    }
  }
}
